// src/hooks/useReferences.ts - Hooks pour la gestion des références (ventes liées aux référents)

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  query,
  where,
  orderBy,
  Timestamp,
  startAt,
  endAt,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { Reference } from '../types/referents';
import { useAuth } from '../contexts/AuthContext';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { usePermissions } from './usePermissions';
import dayjs from 'dayjs';

// ===== HOOKS POUR LES RÉFÉRENCES =====

/**
 * Hook pour récupérer toutes les références d'un business
 */
export const useReferences = (filters?: {
  referentId?: string;
  clientId?: string;
  startDate?: Date;
  endDate?: Date;
  saleType?: 'product' | 'prestation';
}) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['references', businessId, filters],
    queryFn: async () => {
      if (!businessId) {
        return [];
      }

      try {
        let q = query(
          collection(db, COLLECTIONS.references),
          where('businessId', '==', businessId),
          orderBy('saleDate', 'desc')
        );

        if (filters?.referentId) {
          q = query(q, where('referentId', '==', filters.referentId));
        }

        if (filters?.clientId) {
          q = query(q, where('clientId', '==', filters.clientId));
        }

        if (filters?.saleType) {
          q = query(q, where('saleType', '==', filters.saleType));
        }

        if (filters?.startDate) {
          q = query(q, where('saleDate', '>=', Timestamp.fromDate(filters.startDate)));
        }

        if (filters?.endDate) {
          q = query(q, where('saleDate', '<=', Timestamp.fromDate(filters.endDate)));
        }

        const snapshot = await getDocs(q).catch((error: any) => {
          handleFirestoreError(error, 'useReferences - Main query');
          throw error;
        });

        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessId: data.businessId,
            referentId: data.referentId,
            referentName: data.referentName || '',
            saleId: data.saleId,
            saleType: data.saleType,
            saleDate: data.saleDate?.toDate() || new Date(),
            clientId: data.clientId,
            clientName: data.clientName || '',
            saleAmount: data.saleAmount || 0,
            quotaPercentage: data.quotaPercentage || 0,
            quotaAmount: data.quotaAmount || 0,
            createdAt: data.createdAt?.toDate() || new Date(),
            createdBy: data.createdBy,
            receiptNumber: data.receiptNumber || '',
          } as Reference;
        });
      } catch (error) {
        console.error('Erreur dans useReferences:', error);
        return [];
      }
    },
    enabled: !!businessId && (permissions?.canViewReferents ?? false),
  });
};

/**
 * Hook pour récupérer les références d'un référent pour un mois donné
 */
export const useReferencesByReferentAndMonth = (
  referentId: string | undefined,
  month: number,
  year: number
) => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['references', businessId, 'referent', referentId, month, year],
    queryFn: async () => {
      if (!referentId || !businessId) {
        return [];
      }

      try {
        const startDate = dayjs(`${year}-${month.toString().padStart(2, '0')}-01`).startOf('month').toDate();
        const endDate = dayjs(`${year}-${month.toString().padStart(2, '0')}-01`).endOf('month').toDate();

        const q = query(
          collection(db, COLLECTIONS.references),
          where('businessId', '==', businessId),
          where('referentId', '==', referentId),
          where('saleDate', '>=', Timestamp.fromDate(startDate)),
          where('saleDate', '<=', Timestamp.fromDate(endDate)),
          orderBy('saleDate', 'desc')
        );

        const snapshot = await getDocs(q).catch((error: any) => {
          handleFirestoreError(error, 'useReferencesByReferentAndMonth - Main query');
          throw error;
        });

        return snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessId: data.businessId,
            referentId: data.referentId,
            referentName: data.referentName || '',
            saleId: data.saleId,
            saleType: data.saleType,
            saleDate: data.saleDate?.toDate() || new Date(),
            clientId: data.clientId,
            clientName: data.clientName || '',
            saleAmount: data.saleAmount || 0,
            quotaPercentage: data.quotaPercentage || 0,
            quotaAmount: data.quotaAmount || 0,
            createdAt: data.createdAt?.toDate() || new Date(),
            createdBy: data.createdBy,
            receiptNumber: data.receiptNumber || '',
          } as Reference;
        });
      } catch (error) {
        console.error('Erreur dans useReferencesByReferentAndMonth:', error);
        return [];
      }
    },
    enabled: !!referentId && !!businessId && month >= 1 && month <= 12 && year > 0 && (permissions?.canViewReferents ?? false),
  });
};

/**
 * Hook pour créer une référence (appelé automatiquement lors d'une vente)
 */
export const useAddReference = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, userProfile } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async (referenceData: {
      referentId: string;
      referentName: string;
      saleId: string;
      saleType: 'product' | 'prestation';
      saleDate: Date;
      clientId: string;  // ✅ OBLIGATOIRE : pas de référence pour client anonyme
      clientName: string;
      saleAmount: number;
      quotaPercentage: number;
      receiptNumber: string;
    }) => {
      if (!businessId || !userProfile) {
        throw new Error('Business ou utilisateur non défini');
      }

      // Validation : clientId doit être défini (pas de client anonyme)
      if (!referenceData.clientId) {
        throw new Error('Une référence ne peut être créée que pour un client identifié');
      }

      // Calculer le quota
      const quotaAmount = referenceData.saleAmount * (referenceData.quotaPercentage / 100);

      const newReference = {
        businessId,
        referentId: referenceData.referentId,
        referentName: referenceData.referentName.trim(),
        saleId: referenceData.saleId,
        saleType: referenceData.saleType,
        saleDate: Timestamp.fromDate(referenceData.saleDate),
        clientId: referenceData.clientId,
        clientName: referenceData.clientName.trim(),
        saleAmount: referenceData.saleAmount,
        quotaPercentage: referenceData.quotaPercentage,
        quotaAmount,
        receiptNumber: referenceData.receiptNumber,
        createdAt: Timestamp.now(),
        createdBy: userProfile.id,
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.references), newReference).catch((error: any) => {
        handleFirestoreError(error, 'useAddReference - Add document');
        throw error;
      });

      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['references', businessId] });
    },
  });
};

/**
 * Hook pour supprimer une référence (utilisé lors de l'archivage d'une vente)
 */
export const useDeleteReference = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;

  return useMutation({
    mutationFn: async ({ saleId, saleType }: { saleId: string; saleType: 'product' | 'prestation' }) => {
      if (!businessId) {
        throw new Error('Business non défini');
      }

      // Rechercher la référence liée à cette vente
      const q = query(
        collection(db, COLLECTIONS.references),
        where('businessId', '==', businessId),
        where('saleId', '==', saleId),
        where('saleType', '==', saleType)
      );

      const snapshot = await getDocs(q).catch((error: any) => {
        handleFirestoreError(error, 'useDeleteReference - Query');
        throw error;
      });

      // Supprimer toutes les références trouvées (normalement une seule)
      const deleteDoc = (await import('firebase/firestore')).deleteDoc;
      for (const docSnap of snapshot.docs) {
        await deleteDoc(doc(db, COLLECTIONS.references, docSnap.id));
      }

      return snapshot.docs.length;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['references', businessId] });
    },
  });
};
